import React, { useState, useCallback, useMemo } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { 
  Check, ChevronRight, ChevronLeft, Lock, BookOpen, 
  Sparkles, Save, RotateCcw, Plus, Loader2,
  User, Users, Briefcase, Gamepad2, Plane, GraduationCap
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// iFORTE field configuration - static, never changes
const IFORTE_FIELDS = [
  { key: 'identity', letter: 'I', label: 'Identity', subtitle: null, icon: 'user' },
  { key: 'family', letter: 'F', label: 'Family', subtitle: 'Information about your immediate or extended family', icon: 'users' },
  { key: 'occupation', letter: 'O', label: 'Occupation', subtitle: 'Your work, profession, workplace, and daily routine', icon: 'briefcase' },
  { key: 'recreation', letter: 'R', label: 'Recreation', subtitle: 'Hobbies, sports, entertainment', icon: 'gamepad' },
  { key: 'travel', letter: 'T', label: 'Travel', subtitle: 'Places you have visited or would like to visit', icon: 'plane' },
  { key: 'education', letter: 'E', label: 'Education', subtitle: 'Schools, studies, and learning experiences', icon: 'graduation' }
];

const PLACEHOLDERS = {
  identity: `My name is Josh.
I was born in Sydney, Australia.
I am married and have two children.
I am a civil engineer.`,
  family: `My wife's name is Sarah.
My oldest son is called Joseph and my youngest daughter is Jennifer.
My parents are retired and live in London.
My younger brothers live in New Zealand.`,
  occupation: `I work as a civil engineer.
I work for a construction company in Toronto.
My job involves designing bridges and supervising projects.
I usually start work at 8 a.m. and finish around 5 p.m.`,
  recreation: `In my free time I enjoy playing tennis and hiking.
I also like watching movies and reading science fiction books.
On weekends I often spend time with friends.`,
  travel: `I have traveled to Japan, Brazil, and Italy.
I love visiting historical places and trying local food.
One day I would like to visit Iceland and New Zealand.`,
  education: `I studied computer science at the University of Melbourne.
I graduated in 2010.
I also studied Spanish for two years.`
};

// Icon component
const FieldIcon = ({ icon }) => {
  const iconClass = "h-5 w-5 text-red-500";
  switch (icon) {
    case 'user': return <User className={iconClass} />;
    case 'users': return <Users className={iconClass} />;
    case 'briefcase': return <Briefcase className={iconClass} />;
    case 'gamepad': return <Gamepad2 className={iconClass} />;
    case 'plane': return <Plane className={iconClass} />;
    case 'graduation': return <GraduationCap className={iconClass} />;
    default: return null;
  }
};

// Word counter display
const WordCounter = ({ count, target = 150 }) => {
  const percentage = Math.min((count / target) * 100, 100);
  const isComplete = count >= target;
  
  return (
    <div className="flex flex-col items-center gap-2">
      <div className="flex items-center gap-3">
        <div className={`text-2xl font-bold ${isComplete ? 'text-green-500' : 'text-primary'}`}>
          {count}
        </div>
        <div className="text-lg text-muted-foreground">/ {target}</div>
      </div>
      <div className="w-48 h-2 bg-muted rounded-full overflow-hidden">
        <div 
          className={`h-full transition-all duration-300 ${isComplete ? 'bg-green-500' : 'bg-primary'}`}
          style={{ width: `${percentage}%` }}
        />
      </div>
      <div className="text-xs text-muted-foreground">
        {isComplete ? '✓ Ready to generate!' : `${target - count} words needed`}
      </div>
    </div>
  );
};

// Progress indicator
const IForteProgress = ({ currentSlide }) => {
  const slides = [
    { num: 1, letters: 'i — F' },
    { num: 2, letters: 'O — R' },
    { num: 3, letters: 'T — E' }
  ];
  
  return (
    <div className="flex items-center justify-center gap-6 mb-4">
      {slides.map((slide) => (
        <div key={slide.num} className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${
            currentSlide === slide.num 
              ? 'bg-primary text-primary-foreground' 
              : currentSlide > slide.num
                ? 'bg-green-500 text-white'
                : 'bg-muted text-muted-foreground'
          }`}>
            {currentSlide > slide.num ? <Check className="h-4 w-4" /> : slide.num}
          </div>
          <span className={`text-sm font-medium ${currentSlide === slide.num ? 'text-foreground' : 'text-muted-foreground'}`}>
            {slide.letters}
          </span>
          {slide.num < 3 && (
            <div className={`w-8 h-0.5 ${currentSlide > slide.num ? 'bg-green-500' : 'bg-muted'}`} />
          )}
        </div>
      ))}
    </div>
  );
};

// Single text field component
const IForteTextField = React.memo(({ field, value, placeholder, onChange }) => {
  const handleChange = useCallback((e) => {
    onChange(field.key, e.target.value);
  }, [field.key, onChange]);
  
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-red-500/10">
          <FieldIcon icon={field.icon} />
        </div>
        <div>
          <label className="font-semibold">
            <span className="text-red-500 text-xl font-bold">{field.letter}</span> — {field.label}
          </label>
          {field.subtitle && (
            <p className="text-sm text-muted-foreground">{field.subtitle}</p>
          )}
        </div>
      </div>
      <Textarea
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        className="min-h-[120px]"
        data-testid={`iforte-${field.key}`}
      />
    </div>
  );
});

// Slide titles
const SLIDE_TITLES = [
  { prefix: '', highlight: 'i', suffix: 'FORTE — Biographical Information' },
  { prefix: 'i', highlight: 'FOR', suffix: 'TE — Personal Life' },
  { prefix: 'iFOR', highlight: 'TE', suffix: ' — Life Background' }
];

// Privacy Notice Component
const PrivacyNotice = ({ onAgree, onDecline }) => (
  <div className="space-y-8 max-w-2xl mx-auto" data-testid="step-privacy">
    <div className="text-center space-y-3">
      <div className="p-4 rounded-full bg-primary/10 w-fit mx-auto">
        <Lock className="h-12 w-12 text-primary" />
      </div>
      <h2 className="text-2xl font-bold">Your Privacy Matters</h2>
    </div>
    
    <Card className="border-primary/20">
      <CardContent className="pt-6 space-y-4">
        <p className="text-muted-foreground leading-relaxed">
          The information you provide will be used <span className="text-foreground font-medium">only</span> to 
          generate personalized learning material.
        </p>
        <p className="text-muted-foreground leading-relaxed">
          Your data will remain private and will <span className="text-foreground font-medium">never be shared</span>.
        </p>
        
        <div className="pt-4 border-t border-border">
          <p className="text-sm text-muted-foreground">
            If you prefer not to enter real information, you may:
          </p>
          <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
            <li className="flex items-center gap-2">
              <span className="text-primary">•</span>
              invent fictional details
            </li>
            <li className="flex items-center gap-2">
              <span className="text-primary">•</span>
              or choose the custom learning route
            </li>
          </ul>
        </div>
      </CardContent>
    </Card>
    
    <div className="flex flex-col sm:flex-row gap-3 justify-center">
      <Button onClick={onAgree} size="lg" className="gap-2" data-testid="privacy-agree">
        <Check className="h-5 w-5" />
        I agree — Help me communicate faster
      </Button>
      <Button onClick={onDecline} variant="outline" size="lg" className="gap-2" data-testid="privacy-decline">
        <ChevronRight className="h-5 w-5" />
        Move to Custom Lessons
      </Button>
    </div>
  </div>
);

// Data entry component - all slides rendered but only current visible
const IForteDataEntry = ({ currentSlide, data, onUpdateData, wordCount, onNext, onBack, onComplete, canComplete }) => {
  // Group fields by slide
  const slideFields = useMemo(() => ({
    1: IFORTE_FIELDS.slice(0, 2), // identity, family
    2: IFORTE_FIELDS.slice(2, 4), // occupation, recreation
    3: IFORTE_FIELDS.slice(4, 6)  // travel, education
  }), []);
  
  return (
    <div className="space-y-6 max-w-3xl mx-auto">
      <IForteProgress currentSlide={currentSlide} />
      <WordCounter count={wordCount} />
      
      {/* Slide 1 */}
      <div style={{ display: currentSlide === 1 ? 'block' : 'none' }}>
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold">
            <span className="text-red-500">i</span>FORTE — Biographical Information
          </h2>
        </div>
        <div className="space-y-6">
          {slideFields[1].map(field => (
            <IForteTextField
              key={field.key}
              field={field}
              value={data[field.key] || ''}
              placeholder={PLACEHOLDERS[field.key]}
              onChange={onUpdateData}
            />
          ))}
        </div>
      </div>
      
      {/* Slide 2 */}
      <div style={{ display: currentSlide === 2 ? 'block' : 'none' }}>
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold">
            i<span className="text-red-500">FOR</span>TE — Personal Life
          </h2>
        </div>
        <div className="space-y-6">
          {slideFields[2].map(field => (
            <IForteTextField
              key={field.key}
              field={field}
              value={data[field.key] || ''}
              placeholder={PLACEHOLDERS[field.key]}
              onChange={onUpdateData}
            />
          ))}
        </div>
      </div>
      
      {/* Slide 3 */}
      <div style={{ display: currentSlide === 3 ? 'block' : 'none' }}>
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold">
            iFOR<span className="text-red-500">TE</span> — Life Background
          </h2>
        </div>
        <div className="space-y-6">
          {slideFields[3].map(field => (
            <IForteTextField
              key={field.key}
              field={field}
              value={data[field.key] || ''}
              placeholder={PLACEHOLDERS[field.key]}
              onChange={onUpdateData}
            />
          ))}
        </div>
      </div>
      
      {/* Navigation */}
      <div className="flex justify-between pt-4">
        <Button variant="ghost" onClick={onBack} data-testid="prev-btn">
          <ChevronLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        
        {currentSlide < 3 ? (
          <Button onClick={onNext} data-testid="next-btn">
            Next
            <ChevronRight className="h-4 w-4 ml-2" />
          </Button>
        ) : (
          <Button onClick={onComplete} disabled={!canComplete} className="gap-2" data-testid="complete-btn">
            <Sparkles className="h-4 w-4" />
            Generate My First Lessons
          </Button>
        )}
      </div>
    </div>
  );
};

// Generation/Loading screen
const GenerationScreen = ({ message }) => (
  <div className="space-y-6 max-w-2xl mx-auto text-center" data-testid="iforte-generation">
    <div className="p-4 rounded-full bg-primary/10 w-fit mx-auto animate-pulse">
      <Sparkles className="h-12 w-12 text-primary animate-spin" />
    </div>
    <h2 className="text-2xl font-bold">{message}</h2>
    <p className="text-muted-foreground">This may take a moment...</p>
  </div>
);

// Post-lesson options
const PostLessonOptions = ({ lessonNumber, hasMultipleLessons, onStudyNow, onSaveForLater, onReviewPrevious, onCreateAnother }) => (
  <div className="space-y-6 max-w-2xl mx-auto" data-testid="post-lesson-options">
    <div className="text-center space-y-3">
      <div className="p-4 rounded-full bg-green-500/10 w-fit mx-auto">
        <Check className="h-12 w-12 text-green-500" />
      </div>
      <h2 className="text-2xl font-bold">Lesson #{lessonNumber} Created!</h2>
      <p className="text-muted-foreground">
        Your personalized "About Me" lesson is ready. What would you like to do?
      </p>
    </div>
    
    <div className="grid gap-4">
      <Card className="cursor-pointer hover:border-primary transition-colors" onClick={onStudyNow} data-testid="study-now-option">
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-primary/10">
              <BookOpen className="h-6 w-6 text-primary" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold">Study It Now</h3>
              <p className="text-sm text-muted-foreground">
                Start reading and learning from your new lesson immediately
              </p>
            </div>
            <ChevronRight className="h-5 w-5 text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
      
      <Card className="cursor-pointer hover:border-primary transition-colors" onClick={onSaveForLater} data-testid="save-later-option">
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-blue-500/10">
              <Save className="h-6 w-6 text-blue-500" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold">Save for Next Session</h3>
              <p className="text-sm text-muted-foreground">
                Go to your dashboard and study later
              </p>
            </div>
            <ChevronRight className="h-5 w-5 text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
      
      {hasMultipleLessons && (
        <Card className="cursor-pointer hover:border-primary transition-colors" onClick={onReviewPrevious} data-testid="review-previous-option">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-amber-500/10">
                <RotateCcw className="h-6 w-6 text-amber-500" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold">Review Previous Lesson</h3>
                <p className="text-sm text-muted-foreground">
                  Go back to Lesson #{lessonNumber - 1}
                </p>
              </div>
              <ChevronRight className="h-5 w-5 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
      )}
      
      <div className="border-t pt-4 mt-2">
        <Card className="cursor-pointer hover:border-green-500 border-dashed transition-colors" onClick={onCreateAnother} data-testid="create-another-option">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-green-500/10">
                <Plus className="h-6 w-6 text-green-500" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-green-600">Create Another Lesson</h3>
                <p className="text-sm text-muted-foreground">
                  Answer more questions to generate additional personalized content
                </p>
              </div>
              <ChevronRight className="h-5 w-5 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  </div>
);

// Main iFORTE Flow Component
export default function IForteFlow({ 
  selectedLanguages, 
  onComplete, 
  onSwitchToCustom,
  onBack 
}) {
  // Local state for this flow
  const [phase, setPhase] = useState('privacy'); // privacy, data-entry, generating, post-lesson, follow-up
  const [currentSlide, setCurrentSlide] = useState(1);
  const [iforteData, setIforteData] = useState({
    identity: '',
    family: '',
    occupation: '',
    recreation: '',
    travel: '',
    education: ''
  });
  const [generationMessage, setGenerationMessage] = useState('');
  const [createdLessons, setCreatedLessons] = useState([]);
  const [lessonNumber, setLessonNumber] = useState(1);
  const [previousLessonId, setPreviousLessonId] = useState(null);
  
  // Calculate word count
  const wordCount = useMemo(() => {
    return Object.values(iforteData)
      .join(' ')
      .trim()
      .split(/\s+/)
      .filter(w => w.length > 0)
      .length;
  }, [iforteData]);
  
  // Update data handler
  const handleUpdateData = useCallback((field, value) => {
    setIforteData(prev => ({ ...prev, [field]: value }));
  }, []);
  
  // Navigation handlers
  const handleNext = useCallback(() => {
    if (currentSlide < 3) {
      setCurrentSlide(prev => prev + 1);
    }
  }, [currentSlide]);
  
  const handleBack = useCallback(() => {
    if (currentSlide > 1) {
      setCurrentSlide(prev => prev - 1);
    } else {
      // Go back to privacy or main onboarding
      if (phase === 'data-entry') {
        setPhase('privacy');
      } else {
        onBack();
      }
    }
  }, [currentSlide, phase, onBack]);
  
  // Generate lesson
  const handleGenerateLesson = useCallback(async () => {
    setPhase('generating');
    setGenerationMessage('Creating your personalized lesson...');
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/onboarding/iforte/generate-lesson`, {
        iforte_data: iforteData,
        additional_info: null,
        target_languages: selectedLanguages
      }, { withCredentials: true });
      
      if (response.data.success && response.data.texts.length > 0) {
        setCreatedLessons(prev => [...prev, ...response.data.texts]);
        setPreviousLessonId(response.data.texts[0].text_id);
        setLessonNumber(prev => prev + 1);
        setPhase('post-lesson');
        toast.success(`Created ${response.data.texts.length} personalized lesson(s)!`);
      } else {
        toast.error('Failed to generate lesson');
        setPhase('data-entry');
      }
    } catch (err) {
      console.error('Error generating lesson:', err);
      toast.error('Failed to generate your personalized lesson');
      setPhase('data-entry');
    }
  }, [iforteData, selectedLanguages]);
  
  // Post-lesson handlers
  const handleStudyNow = useCallback(async () => {
    await onComplete();
    if (previousLessonId) {
      window.location.href = `/reader/${previousLessonId}`;
    } else {
      window.location.href = '/dashboard';
    }
  }, [previousLessonId, onComplete]);
  
  const handleSaveForLater = useCallback(async () => {
    await onComplete();
    toast.success('Lesson saved! You can find it in your library.');
    window.location.href = '/dashboard';
  }, [onComplete]);
  
  const handleReviewPrevious = useCallback(() => {
    if (createdLessons.length > 1) {
      const prevLesson = createdLessons[createdLessons.length - 2];
      window.location.href = `/reader/${prevLesson.text_id}`;
    }
  }, [createdLessons]);
  
  const handleCreateAnother = useCallback(() => {
    // Reset data and go back to data entry
    setIforteData({
      identity: '',
      family: '',
      occupation: '',
      recreation: '',
      travel: '',
      education: ''
    });
    setCurrentSlide(1);
    setPhase('data-entry');
  }, []);
  
  // Render based on phase
  switch (phase) {
    case 'privacy':
      return (
        <PrivacyNotice 
          onAgree={() => setPhase('data-entry')} 
          onDecline={onSwitchToCustom}
        />
      );
      
    case 'data-entry':
      return (
        <IForteDataEntry
          currentSlide={currentSlide}
          data={iforteData}
          onUpdateData={handleUpdateData}
          wordCount={wordCount}
          onNext={handleNext}
          onBack={handleBack}
          onComplete={handleGenerateLesson}
          canComplete={wordCount >= 150}
        />
      );
      
    case 'generating':
      return <GenerationScreen message={generationMessage} />;
      
    case 'post-lesson':
      return (
        <PostLessonOptions
          lessonNumber={lessonNumber - 1}
          hasMultipleLessons={createdLessons.length > 1}
          onStudyNow={handleStudyNow}
          onSaveForLater={handleSaveForLater}
          onReviewPrevious={handleReviewPrevious}
          onCreateAnother={handleCreateAnother}
        />
      );
      
    default:
      return null;
  }
}
